import { Align } from 'chart.js';

import { FormatProperties } from 'models/response/modelOutput.interface';

import formatValue, { withCurrencySymbol } from './FormatValues';

interface Properties {
  chartType?: string;
  isLegend?: boolean;
  pageWidth?: number;
  formatProperties?: FormatProperties;
  pageName?: string;
  aspectRatio?: number;
  axisTheme?: 'dark' | 'light';
}

const getChartOptions = ({
  chartType = 'line',
  isLegend = false,
  pageWidth = 0,
  formatProperties,
  pageName = '',
  aspectRatio,
  axisTheme = 'dark',
}: Properties) => {
  const normalizedFormatProperties = withCurrencySymbol(formatProperties);
  const isLightAxis = axisTheme === 'light';
  const axisLabelColor = isLightAxis ? '#121826' : 'white';
  const yAxisLabelColor = isLightAxis ? '#121826' : '#cdcdcd66';
  const xGridBorderColor = isLightAxis ? '#E8E9EB' : '#ffffff66';
  const yGridColor = isLightAxis ? '#E8E9EB' : '#ffffff33';
  const legendLabelColor = isLightAxis ? '#121826' : '#ffffff';

  return {
    aspectRatio,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      tooltip: {
        callbacks: {
          label(context: any) {
            const label = context.dataset.label || '';
            const value = context.formattedValue || '';

            if (formatProperties?.measure === '%') {
              return `${label}: ${value * 100}`;
            }
            return `${label}: ${value}`;
          },
        },
      },
      legend: {
        display: isLegend,
        position: 'top' as const,
        align: 'start' as Align,
        labels: {
          boxWidth: pageWidth < 576 ? 10 : 15,
          boxHeight: pageWidth < 576 ? 10 : 15,
          color: legendLabelColor,
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: chartType === 'stacked bar',
        grid: {
          color: 'transparent',
          borderColor: xGridBorderColor,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          color: axisLabelColor,
          padding: 10,
        },
      },
      y: {
        beginAtZero: true,
        stacked: chartType === 'stacked bar',
        grid: {
          color: yGridColor,
          borderColor: 'transparent',
          tickColor: 'transparent',
        },
        ticks: {
          color: yAxisLabelColor,
          padding: 0,
          callback(value: any) {
            if (pageName === 'drivers') {
              if (formatProperties?.measure === '%') {
                return `${(value * 100).toFixed(2)}%`;
              }
              return `${formatValue(
                value,
                (normalizedFormatProperties ?? formatProperties) as FormatProperties,
              )}`;
            }

            return value.toLocaleString();
          },
        },
      },
    },
  };
};

export default getChartOptions;
